import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import {
  FaCogs,
  FaInfoCircle,
  FaClock,
  FaCheckCircle,
  FaExclamationTriangle,
  FaSearch,
  FaUndoAlt,
  FaUser,
  FaWarehouse,
  FaStore,
  FaBuilding,
  FaMobileAlt,
  FaExchangeAlt,
  FaTimes,
} from "react-icons/fa";
import turkishCoffeeMachine from "../../../../Assets/icons/turkish-coffee-machine.png";
import teaFilterCoffeeMachine from "../../../../Assets/icons/tea-filter-coffee-machine-icon.png";
import teaTurkishCoffeeMachine from "../../../../Assets/icons/tea_coffee_machine.png";
import espressoMachine from "../../../../Assets/icons/espresso_machine.png";
import useAuth from "../../../../Hooks/useAuth";
import usePermission from "../../../../Hooks/usePermission";
import {
  STEP_TYPE_LABELS,
  STEP_KEY_LABELS,
  getMetadataLabel,
} from "../../../../utils/operationMetadata";
import { useFirmReportData as defaultUseFirmReportData } from "../../../../Hooks/useFirmReportData";
import {
  formatToTurkeyDate,
} from "../../../Utils/FormatDateHourString";
import ReportTable from "../../../Reports/ReportTable";
import PerformanceReportView from "../../../Reports/PerformanceReportView";
import useDetailPerformanceReport from "../../../../Hooks/useDetailPerformanceReport";
import { networkMinDate, clampToCreation } from "../../../../utils/creationInfo";
import { useFirmData } from "../../../../Hooks/useFirmData";
import { PRODUCT_OPTIONS } from "../../../../Pages/Reports/Firm/performanceReportOptions";
import { firmHasActiveProduct } from "../../../../utils/consumptionProductGroup";
import ExportExcel from "../../../Utils/ExportExcel";
import { CustomDropdown, DateRangeField } from "../../../atoms";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import trLocale from "date-fns/locale/tr";
import mascotNoData from "../../../../Assets/icons/okkali_mascot_nodata.png";
import useDrawerTransition from "../../../../Hooks/useDrawerTransition";
import ConsumentDeviceDrawer from "../ConsumentDeviceDrawer";
import { groupConsumptionProduct } from "../../../../utils/consumptionProductGroup";
import { getFirmIcon } from "../../../../utils/firmUtils";
import {
  displayLogInfoName,
  displayLogText,
  displayLogValue,
} from "../../../../utils/deviceActivityLabels";

// Cihaz listesindeki makine gorselleriyle ayni harita (model adina gore)
const DEVICE_IMAGES = {
  Fincanda: turkishCoffeeMachine,
  Çayset: teaFilterCoffeeMachine,
  "Çayset-Fincanda": teaTurkishCoffeeMachine,
  Espresso: espressoMachine,
};

// METADATA_KEY_LABELS burada YEREL olarak tekrar tanımlanıyordu ve cihaz
// raporundaki sözlükle drift etmişti (ör. index: "Sıra" vs "Göz",
// consument: "Tüketim" vs ürüne göre Demlenen/Pişirilen). Artık tek kaynak:
// utils/operationMetadata.

// Kota Değişikliği kaynak etiketleri — server `source` enum'u ile birebir
// (modules/firm/data/models/firmQuotaChangeLog.model.js).
const QUOTA_SOURCE_LABELS = {
  Bill: "Fatura",
  BillDelete: "Fatura Silme",
  ManualTransfer: "Elle Transfer",
  FreeUpload: "Bedelsiz Yükleme",
  GetReturn: "İade",
  QuotaShare: "Kota Paylaşımı / Satışı",
  AutoDistribution: "Otomatik Dağıtım",
  DeviceReturn: "Cihazdan İade",
  OfflineConsumption: "Çevrimdışı Tüketim",
  SettingChange: "Dağıtım Ayarı",
  ProductDelete: "Ürün Silme",
};

const QUOTA_SOURCE_COLORS = {
  Bill: "bg-green-100 text-green-700",
  BillDelete: "bg-rose-100 text-rose-700",
  ManualTransfer: "bg-indigo-100 text-indigo-700",
  FreeUpload: "bg-yellow-100 text-yellow-700",
  GetReturn: "bg-red-100 text-red-700",
  QuotaShare: "bg-indigo-100 text-indigo-700",
  AutoDistribution: "bg-blue-100 text-blue-700",
  DeviceReturn: "bg-teal-100 text-teal-700",
  OfflineConsumption: "bg-orange-100 text-orange-700",
  SettingChange: "bg-purple-100 text-purple-700",
  ProductDelete: "bg-gray-200 text-gray-700",
};

// Fatura hareketi logu operasyonlari — server: firmUpdateBillLog.model.js
// `updateInfoSchema.operation` enum'u. Yeni tip eklenirse IKI tarafi da guncelle.
const BILL_LOG_OPERATION_LABELS = {
  Added: "Ekleme",
  FreeUpload: "Bedelsiz Yükleme",
  GetReturn: "İade",
  QuotaShare: "Kota Paylaşımı / Satışı",
  Deleted: "Silme",
};

const BILL_LOG_OPERATION_COLORS = {
  Added: "bg-green-100 text-green-700",
  FreeUpload: "bg-yellow-100 text-yellow-700",
  GetReturn: "bg-red-100 text-red-700",
  QuotaShare: "bg-indigo-100 text-indigo-700",
  Deleted: "bg-gray-100 text-gray-700",
};

const OPERATIONS_HIDE_META = ["CleaningSave"];
const isOperationHidingMeta = (operationName) => {
  if (!operationName) return false;
  if (OPERATIONS_HIDE_META.includes(operationName)) return true;
  const lower = operationName.toLowerCase();
  if (lower.includes("potcalibration") || lower.includes("demlikkalibrasyon")) return true;
  if (lower.includes("weightcalibration") || lower.includes("ağırlıkkalibrasyon") || lower.includes("cupcalibration")) return true;
  return false;
};

const METADATA_HIDDEN_KEYS = ["operationKey"];

const ReportTab = ({ firm, useReportHook }) => {
  const { auth } = useAuth();
  const { hasPermission } = usePermission();
  const useFirmReportData = useReportHook || defaultUseFirmReportData;
  const {
    input,
    setInput,
    filterType,
    setDate,
    setDateSecond,
    Datas,
    isFetching,
    isLoading,
    isError,
    handleGetLogData,
    handleFilterType,
    firmOptions,
    setFirmIds,
  } = useFirmReportData(firm?._id, auth.accessToken);

  // Firma kapsamı seçimi: "" = Tüm Firmalar (varsayılan), aksi halde tek firma.
  // Rapor tablosu tek firma kırılımı göstermediği için çoklu seçim yerine
  // "tümü ya da biri" tercih edildi (kullanıcı kararı 2026-09-14).
  const [selectedScopeFirm, setSelectedScopeFirm] = useState("");
  const firmScopeOptions = useMemo(
    () => [
      { id: "all", label: "Tüm Firmalar", value: "" },
      // Kök bayi ise liste bayi ağıdır (#200): tip ikonu + bayilerde "Bayi".
      ...(firmOptions || []).map((f) => ({
        id: f._id,
        label: f.firmName,
        value: f._id,
        icon: f.firmType ? getFirmIcon(f, "w-3.5 h-3.5") : undefined,
        description: f.firmType === "dealer" ? "Bayi" : undefined,
      })),
    ],
    [firmOptions],
  );

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // "Tüketim" = Müşteri Performans raporu bu firma için (alt firmalar / bayi
  // ağı dahil), PDF + Excel (#343); eski tablo "Tüketim Detayı" (Consument)
  const detailReport = useDetailPerformanceReport("firm");
  const isDetailReport = input.type === "ConsumentReport";
  // Bayide ürün müşterilerinde açık olabilir — liste süzülmez
  const productOptions = useMemo(() => {
    if (firm?.firmType === "dealer") return PRODUCT_OPTIONS;
    const active = PRODUCT_OPTIONS.filter((o) => firmHasActiveProduct(firm, o.value));
    return active.length > 0 ? active : PRODUCT_OPTIONS;
  }, [firm]);
  const [selectedProduct, setSelectedProduct] = useState("");
  useEffect(() => {
    if (!productOptions.some((o) => o.value === selectedProduct)) {
      setSelectedProduct(productOptions[0]?.value || "");
    }
  }, [productOptions, selectedProduct]);
  // Rapor alt firmaları / bayinin müşterilerini de kapsar: alt sınır firmanın
  // ağındaki (alt + üst firmalar) en eski oluşturulma günü (#343, #270)
  const { firms: networkFirms } = useFirmData(auth.accessToken);
  const minReportTime = networkMinDate(firm, networkFirms)?.getTime();
  const minReportDate = useMemo(
    () => (minReportTime !== undefined ? new Date(minReportTime) : undefined),
    [minReportTime],
  );
  const [selectedOperationLog, setSelectedOperationLog] = useState(null);
  const [showOperationDrawer, setShowOperationDrawer] = useState(false);
  // Kota Değişikliği satır detayı — tabloya sığmayan alanlar (firma havuzunun
  // önce/sonrası, fatura no, tam açıklama) burada gösterilir.
  const [selectedQuotaLog, setSelectedQuotaLog] = useState(null);
  const [showQuotaDrawer, setShowQuotaDrawer] = useState(false);
  // Tuketim satirina tiklaninca acilan CIHAZ KIRILIMI drawer'i.
  const [consumentRow, setConsumentRow] = useState(null);
  // Operasyon drawer'ı da diğerleriyle aynı giriş/çıkış kaymasını kullanır.
  const { mounted: opDrawerMounted, entered: opDrawerEntered } =
    useDrawerTransition(showOperationDrawer);
  const { mounted: quotaDrawerMounted, entered: quotaDrawerEntered } =
    useDrawerTransition(showQuotaDrawer);

  const allReportTypeOptions = [
    { id: "ConsumentReport", label: "Tüketim", value: "ConsumentReport", permission: "firms.logConsumption" },
    { id: "Consument", label: "Tüketim Detayı", value: "Consument", permission: "firms.logConsumption" },
    { id: "UpdateInfo", label: "Bilgi Değişikliği", value: "UpdateInfo", permission: "firms.logInfo" },
    { id: "UpdateBill", label: "Fatura Hareketi", value: "UpdateBill", permission: "firms.logBill" },
    { id: "SettingChange", label: "Ayar Değişiklikleri", value: "SettingChange", permission: "firms.logSettings" },
    { id: "Operation", label: "Operasyonlar", value: "Operation", permission: "firms.logOperation" },
    { id: "DeviceMovement", label: "Cihaz Hareketleri", value: "DeviceMovement", permission: "firms.logInfo" },
    { id: "QuotaChange", label: "Kota Değişikliği", value: "QuotaChange", permission: "firms.logQuota" },
  ];
  const reportTypeOptions = allReportTypeOptions.filter((opt) => hasPermission(opt.permission));

  useEffect(() => {
    if (!input.type && reportTypeOptions.length > 0) {
      setInput((prev) => ({ ...prev, type: reportTypeOptions[0].value }));
    }
  }, [input.type, setInput, reportTypeOptions]);

  useEffect(() => {
    const today = new Date();
    const firstDay = clampToCreation(new Date(today.getFullYear(), today.getMonth(), 1), minReportDate);
    setDate(firstDay);
    setStartDate(firstDay);
    setDateSecond(today);
    setEndDate(today);
  }, [setDate, setDateSecond, minReportDate]);

  const filterTypeOptions = [
    { id: "Hourly", label: "Saatlik", value: "Hourly" },
    { id: "Day", label: "Günlük", value: "Day" },
    { id: "Month", label: "Aylık", value: "Month" },
    { id: "Year", label: "Yıllık", value: "Year" },
  ];

  const flattenLogs = useCallback(() => {
    if (!Datas || Datas.length === 0) return [];
    const allLogs = [];
    Datas.forEach((dayData) => {
      if (dayData.logs && Array.isArray(dayData.logs)) {
        dayData.logs.forEach((log) => {
          allLogs.push({ ...log, _dayDate: dayData.date });
        });
      }
    });
    return allLogs;
  }, [Datas]);

  const getTableData = useCallback(() => {
    const allLogs = flattenLogs();
    if (allLogs.length === 0) return [];

    if (input.type === "Consument") {
      if (filterType === "Hourly") {
        return allLogs.map((log) => {
          const logDate = log.createdInfo ? new Date(log.createdInfo) : null;
          return {
            date: logDate
              ? logDate.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })
              : formatToTurkeyDate(log._dayDate) || "-",
            hour: logDate
              ? logDate.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })
              : "-",
            // Çay / Filtre Kahve / Türk Kahvesi dışındaki her ürün tek
            // "Espresso" satırında toplanır (bkz. consumptionProductGroup).
            productName: groupConsumptionProduct(log.productName),
            consumption: log.consument || 0,
            // Satira tiklaninca acilan drawer, tuketimi CIHAZ SERI NO bazinda
            // ve o andaki aktiflik durumuyla gosteriyor. Ham loglar satirda
            // tasinir; ikinci bir istek atilmaz.
            _logs: [log],
          };
        });
      }

      if (filterType === "Day") {
        const dailyMap = {};
        allLogs.forEach((log) => {
          const logDate = log.createdInfo ? new Date(log.createdInfo) : null;
          const dateKey = logDate
            ? logDate.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })
            : log._dayDate || "-";
          if (!dailyMap[dateKey]) dailyMap[dateKey] = {};
          const pName = groupConsumptionProduct(log.productName);
          dailyMap[dateKey][pName] = (dailyMap[dateKey][pName] || 0) + (log.consument || 0);
          // Drawer icin ham loglar — cihaz kirilimi bunlardan uretilir.
          (dailyMap[dateKey]._logs || (dailyMap[dateKey]._logs = [])).push(log);
        });
        return Object.entries(dailyMap).map(([dateKey, bucket]) => {
          const { _logs = [], ...products } = bucket;
          const totalConsumption = Object.values(products).reduce((s, v) => s + v, 0);
          return {
            date: dateKey,
            products,
            _logs,
            totalConsumption,
            hourlyAvg: totalConsumption > 0 ? (totalConsumption / 24).toFixed(1) : "0",
          };
        });
      }

      if (filterType === "Month") {
        const monthlyMap = {};
        allLogs.forEach((log) => {
          const logDate = log.createdInfo ? new Date(log.createdInfo) : null;
          if (!logDate) return;
          const monthKey = logDate.toLocaleDateString("tr-TR", { month: "long", year: "numeric" });
          if (!monthlyMap[monthKey]) monthlyMap[monthKey] = {};
          const pName = groupConsumptionProduct(log.productName);
          monthlyMap[monthKey][pName] = (monthlyMap[monthKey][pName] || 0) + (log.consument || 0);
          // Drawer icin ham loglar — cihaz kirilimi bunlardan uretilir.
          (monthlyMap[monthKey]._logs || (monthlyMap[monthKey]._logs = [])).push(log);
        });
        return Object.entries(monthlyMap).map(([monthKey, bucket]) => {
          const { _logs = [], ...products } = bucket;
          const totalConsumption = Object.values(products).reduce((s, v) => s + v, 0);
          const dayCount = new Set(
            allLogs
              .filter((l) => {
                const d = l.createdInfo ? new Date(l.createdInfo) : null;
                return d && d.toLocaleDateString("tr-TR", { month: "long", year: "numeric" }) === monthKey;
              })
              .map((l) => new Date(l.createdInfo).toDateString())
          ).size;
          return {
            date: monthKey,
            products,
            _logs,
            totalConsumption,
            dailyAvg: dayCount > 0 ? (totalConsumption / dayCount).toFixed(1) : "0",
          };
        });
      }

      if (filterType === "Year") {
        const yearlyMap = {};
        allLogs.forEach((log) => {
          const logDate = log.createdInfo ? new Date(log.createdInfo) : null;
          if (!logDate) return;
          const yearKey = logDate.getFullYear().toString();
          if (!yearlyMap[yearKey]) yearlyMap[yearKey] = {};
          const pName = groupConsumptionProduct(log.productName);
          yearlyMap[yearKey][pName] = (yearlyMap[yearKey][pName] || 0) + (log.consument || 0);
          // Drawer icin ham loglar — cihaz kirilimi bunlardan uretilir.
          (yearlyMap[yearKey]._logs || (yearlyMap[yearKey]._logs = [])).push(log);
        });
        return Object.entries(yearlyMap).map(([yearKey, bucket]) => {
          const { _logs = [], ...products } = bucket;
          const totalConsumption = Object.values(products).reduce((s, v) => s + v, 0);
          const monthCount = new Set(
            allLogs
              .filter((l) => {
                const d = l.createdInfo ? new Date(l.createdInfo) : null;
                return d && d.getFullYear().toString() === yearKey;
              })
              .map((l) => new Date(l.createdInfo).getMonth())
          ).size;
          return {
            date: yearKey,
            products,
            _logs,
            totalConsumption,
            monthlyAvg: monthCount > 0 ? (totalConsumption / monthCount).toFixed(1) : "0",
          };
        });
      }

      return allLogs.map((log) => {
        const logDate = log.createdInfo ? new Date(log.createdInfo) : null;
        return {
          _logs: [log],
          date: logDate
            ? logDate.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })
            : formatToTurkeyDate(log._dayDate) || "-",
          hour: logDate
            ? logDate.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })
            : "-",
          productName: log.productName || "-",
          consumption: log.consument || 0,
        };
      });
    }

    if (input.type === "DeviceMovement") {
      // Firmaya gelen / firmadan giden cihaz hareketleri (server düz satır döner)
      const thisFirmName = firm?.firmName || "Bu firma";
      const thisFirmType = firm?.firmType || "";
      return allLogs.map((log) => {
        const logDate = log.createdInfo ? new Date(log.createdInfo) : null;
        const otherName = log.otherFirmName
          ? `${log.otherFirmName}${log.otherFirmBayserNo ? ` (#${log.otherFirmBayserNo})` : ""}`
          : "-";
        const otherType = log.otherFirmType || "";
        const isIncoming = log.direction === "Gelen";
        // Yön etiketi karşı firmanın tipine göre: Depoya/Bayiye/Müşteriye (giden)
        // veya Depodan/Bayiden/Müşteriden (gelen). Tip bilinmiyorsa Gelen/Giden.
        const TYPE_TO = { storage: "Depoya", dealer: "Bayiye", customer: "Müşteriye" };
        const TYPE_FROM = { storage: "Depodan", dealer: "Bayiden", customer: "Müşteriden" };
        const directionLabel = isIncoming
          ? TYPE_FROM[otherType] || "Gelen"
          : TYPE_TO[otherType] || "Giden";
        return {
          direction: log.direction || "-",
          directionLabel,
          serialNo: log.serialNo || "-",
          deviceName: log.deviceName || "-",
          deviceModel: log.deviceModel || log.deviceName || "",
          fromFirm: isIncoming ? otherName : thisFirmName,
          fromFirmType: isIncoming ? otherType : thisFirmType,
          toFirm: isIncoming ? thisFirmName : otherName,
          toFirmType: isIncoming ? thisFirmType : otherType,
          description: displayLogText(log.description) || "-",
          byName: [log.name, log.lastName].filter(Boolean).join(" ") || "-",
          note: displayLogText(log.note) || "-",
          date: logDate
            ? logDate.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })
            : "-",
          hour: logDate
            ? logDate.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })
            : "-",
          originalData: log,
        };
      });
    }

    if (input.type === "UpdateInfo") {
      const rows = [];
      allLogs.forEach((log) => {
        if (log.info && Array.isArray(log.info)) {
          log.info.forEach((infoItem) => {
            const logDate = log.createdInfo ? new Date(log.createdInfo) : null;
            rows.push({
              name: log.name || "-",
              lastName: log.lastName || "-",
              infoName: displayLogInfoName(infoItem.infoName) || "-",
              valueFrom: displayLogValue(infoItem.infoName, infoItem.valueFrom) || "-",
              valueTo: displayLogValue(infoItem.infoName, infoItem.valueTo) || "-",
              date: logDate
                ? logDate.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })
                : "-",
              hour: logDate
                ? logDate.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })
                : "-",
            });
          });
        }
      });
      return rows;
    }

    if (input.type === "QuotaChange") {
      // Kota hareketleri: fatura/bedelsiz/iade + otomatik dağıtımda hangi
      // makineye ne gitti, neyi geri aldık. "Bilgi Değişikliği"nden ayrıldı.
      return allLogs.map((log) => {
        const logDate = log.createdInfo ? new Date(log.createdInfo) : null;
        const delta = Number(log.quotaDelta) || 0;
        const sourceLabel = QUOTA_SOURCE_LABELS[log.source] || log.source || "-";
        const targetLabel =
          log.scope === "Device"
            ? log.serialNo || "Makine"
            : "Firma havuzu";
        return {
          source: sourceLabel,
          sourceColor: QUOTA_SOURCE_COLORS[log.source] || "bg-gray-100 text-gray-700",
          target: targetLabel,
          scope: log.scope || "Firm",
          serialNo: log.serialNo || "-",
          productName: log.productName || "-",
          quotaDelta: delta,
          quotaDeltaLabel: `${delta > 0 ? "+" : ""}${delta}`,
          quotaDeltaColor: delta > 0 ? "text-green-600" : delta < 0 ? "text-red-600" : "text-gray-600",
          quotaFrom: log.quotaFrom === null || log.quotaFrom === undefined ? "-" : log.quotaFrom,
          quotaTo: log.quotaTo === null || log.quotaTo === undefined ? "-" : log.quotaTo,
          firmQuotaFrom: log.firmQuotaFrom === null || log.firmQuotaFrom === undefined ? "-" : log.firmQuotaFrom,
          firmQuotaTo: log.firmQuotaTo === null || log.firmQuotaTo === undefined ? "-" : log.firmQuotaTo,
          billNo: log.billNo || "-",
          description: displayLogText(log.description) || "-",
          name: log.name || "-",
          lastName: log.lastName || "-",
          byName: [log.name, log.lastName].filter(Boolean).join(" ") || "-",
          date: logDate
            ? logDate.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })
            : "-",
          hour: logDate
            ? logDate.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })
            : "-",
          originalData: log,
        };
      });
    }

    if (input.type === "UpdateBill") {
      return allLogs.map((log) => {
        // Fatura hareketi operasyonlari — server enum'u ile birebir
        // (firmUpdateBillLog.model.js). Eskiden ic ice ternary'di ve
        // BILINMEYEN her deger "Silme" olarak etiketleniyordu; "QuotaShare"
        // eklenince bu sessizce yanlis okunurdu. Artik esleme tablosu:
        // tanimsiz operasyon "Silme" degil, ham deger olarak gorunur.
        const operationLabel = BILL_LOG_OPERATION_LABELS[log.operation]
          ?? (log.operation === "Deleted" ? "Silme" : log.operation || "-");
        const operationColor =
          BILL_LOG_OPERATION_COLORS[log.operation] || "bg-gray-100 text-gray-700";
        return {
          name: log.name || "-",
          lastName: log.lastName || "-",
          operation: operationLabel,
          operationColor,
          billNo: log.info?.billNo || "-",
          productCode: log.info?.productCode || "-",
          productName: log.info?.productName || "-",
          quota: log.info?.quota || "-",
          income: log.info?.income || "-",
          date: log.createdInfo
            ? new Date(log.createdInfo).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })
            : "-",
          hour: log.createdInfo
            ? new Date(log.createdInfo).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })
            : "-",
        };
      });
    }

    if (input.type === "SettingChange") {
      const rows = [];
      allLogs.forEach((log) => {
        if (log.info && Array.isArray(log.info)) {
          log.info.forEach((infoItem) => {
            const logDate = log.createdInfo ? new Date(log.createdInfo) : null;
            rows.push({
              name: log.name || "-",
              lastName: log.lastName || "-",
              serialNo: log.serialNo || "-",
              infoName: displayLogInfoName(infoItem.infoName) || "-",
              valueFrom: displayLogValue(infoItem.infoName, infoItem.valueFrom) || "-",
              valueTo: displayLogValue(infoItem.infoName, infoItem.valueTo) || "-",
              date: logDate
                ? logDate.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })
                : "-",
              hour: logDate
                ? logDate.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })
                : "-",
            });
          });
        }
      });
      return rows;
    }

    if (input.type === "Operation") {
      return allLogs.map((log) => {
        const logDate = log.createdInfo ? new Date(log.createdInfo) : null;
        return {
          operationName: log.operationName || "-",
          description: displayLogText(log.description) || "-",
          serialNo: log.serialNo || "-",
          date: logDate
            ? logDate.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })
            : "-",
          hour: logDate
            ? logDate.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })
            : "-",
          originalData: log,
        };
      });
    }

    return [];
  }, [flattenLogs, input.type, filterType]);

  const getTableColumns = () => {
    if (input.type === "Consument") {
      if (filterType === "Hourly") {
        return [
          { key: "date", label: "Tarih", align: "left" },
          { key: "hour", label: "Saat", align: "center" },
          { key: "productName", label: "Ürün", align: "left" },
          { key: "consumption", label: "Tüketim", align: "center" },
        ];
      }
      if (filterType === "Day") {
        return [
          { key: "date", label: "Tarih", align: "left" },
          { key: "products", label: "Ürün Bazında Tüketim", align: "center" },
        ];
      }
      if (filterType === "Month") {
        return [
          { key: "date", label: "Ay", align: "left" },
          { key: "products", label: "Ürün Bazında Tüketim", align: "center" },
          { key: "dailyAvg", label: "Günlük Ortalama", align: "center" },
        ];
      }
      if (filterType === "Year") {
        return [
          { key: "date", label: "Yıl", align: "left" },
          { key: "products", label: "Ürün Bazında Tüketim", align: "center" },
          { key: "monthlyAvg", label: "Aylık Ortalama", align: "center" },
        ];
      }
      return [
        { key: "date", label: "Tarih", align: "left" },
        { key: "hour", label: "Saat", align: "center" },
        { key: "productName", label: "Ürün", align: "left" },
        { key: "consumption", label: "Tüketim", align: "center" },
      ];
    }
    if (input.type === "DeviceMovement") {
      return [
        { key: "deviceCell", label: "Cihaz", align: "left" },
        { key: "firmMovement", label: "Firma Hareketi", align: "left", cellClassName: "whitespace-normal min-w-0 max-w-[340px]" },
        { key: "description", label: "İşlem Detayı", align: "left", cellClassName: "whitespace-normal min-w-0 max-w-[300px]" },
        { key: "byName", label: "İşlemi Yapan", align: "left" },
        { key: "note", label: "Not", align: "left", cellClassName: "whitespace-normal min-w-0 max-w-[220px]" },
        { key: "dateTime", label: "Tarih / Saat", align: "center" },
      ];
    }
    if (input.type === "UpdateInfo") {
      return [
        { key: "name", label: "İsim", align: "left" },
        { key: "lastName", label: "Soyisim", align: "left" },
        { key: "infoName", label: "Bilgi", align: "left", cellClassName: "whitespace-normal min-w-0 max-w-[280px]" },
        { key: "valueFrom", label: "İlk Değer", align: "center" },
        { key: "valueTo", label: "Son Değer", align: "center" },
        { key: "date", label: "Tarih", align: "center" },
        { key: "hour", label: "Saat", align: "center" },
      ];
    }
    if (input.type === "QuotaChange") {
      return [
        { key: "source", label: "İşlem", align: "center" },
        { key: "target", label: "Nereye / Nereden", align: "left" },
        { key: "productName", label: "Ürün", align: "left" },
        { key: "quotaDeltaLabel", label: "Değişim", align: "center" },
        { key: "quotaFrom", label: "Önceki", align: "center" },
        { key: "quotaTo", label: "Sonraki", align: "center" },
        { key: "description", label: "Açıklama", align: "left", cellClassName: "whitespace-normal min-w-0 max-w-[340px]" },
        { key: "byName", label: "İşlemi Yapan", align: "left" },
        { key: "date", label: "Tarih", align: "center" },
        { key: "hour", label: "Saat", align: "center" },
      ];
    }
    if (input.type === "UpdateBill") {
      return [
        { key: "name", label: "İsim", align: "left" },
        { key: "lastName", label: "Soyisim", align: "left" },
        { key: "operation", label: "İşlem", align: "center" },
        { key: "billNo", label: "Fatura No", align: "left" },
        { key: "productName", label: "Ürün", align: "left" },
        { key: "quota", label: "Miktar", align: "center" },
        { key: "income", label: "Gelir", align: "center" },
        { key: "date", label: "Tarih", align: "center" },
        { key: "hour", label: "Saat", align: "center" },
      ];
    }
    if (input.type === "SettingChange") {
      return [
        { key: "serialNo", label: "Seri No", align: "left" },
        { key: "name", label: "İsim", align: "left" },
        { key: "lastName", label: "Soyisim", align: "left" },
        { key: "infoName", label: "Ayar", align: "left", cellClassName: "whitespace-normal min-w-0 max-w-[280px]" },
        { key: "valueFrom", label: "Eski Değer", align: "center", cellClassName: "whitespace-normal min-w-0 max-w-[180px]" },
        { key: "valueTo", label: "Yeni Değer", align: "center", cellClassName: "whitespace-normal min-w-0 max-w-[180px]" },
        { key: "date", label: "Tarih", align: "center" },
        { key: "hour", label: "Saat", align: "center" },
      ];
    }
    if (input.type === "Operation") {
      return [
        { key: "serialNo", label: "Seri No", align: "left" },
        { key: "operationName", label: "Operasyon", align: "left" },
        { key: "description", label: "Açıklama", align: "left", cellClassName: "whitespace-normal min-w-0 max-w-[320px]" },
        { key: "date", label: "Tarih", align: "center" },
        { key: "hour", label: "Saat", align: "center" },
      ];
    }
    return [];
  };

  // --- Drawer yardımcı fonksiyonları (cihaz ReportTab ile aynı) ---

  const formatDateTime = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return `${d.toLocaleDateString("tr-TR")} ${d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}`;
  };

  // Adım tipi/alan etiketleri utils/operationMetadata'dan (tek kaynak) gelir.

  const humanizeKey = (value) => {
    if (!value) return "-";
    const withSpaces = String(value)
      .replace(/_/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2");
    return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
  };

  const getStepLabel = (type) => {
    if (!type) return "Bilgi";
    return STEP_TYPE_LABELS[type] || humanizeKey(type);
  };

  const formatStepValue = (key, value) => {
    if (value == null || value === "") return "-";
    if (typeof value === "boolean") return value ? "Evet" : "Hayır";
    if (typeof value === "number") {
      if (key === "temperature" || key?.toLowerCase().includes("temperature")) {
        return `${value}°C`;
      }
      return String(value);
    }
    if (value instanceof Date) return formatDateTime(value);
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  };

  const getStepKeyLabel = (key) => STEP_KEY_LABELS[key] || humanizeKey(key);

  const getStepStyle = (type) => {
    const normalizedType = String(type || "").toLowerCase();
    if (normalizedType.includes("completed") || normalizedType.includes("success")) {
      return {
        icon: <FaCheckCircle className="text-green-500 w-4 h-4" />,
        bg: "bg-green-50 border-green-200",
        text: "text-green-700",
      };
    }
    if (normalizedType.includes("failed") || normalizedType.includes("warning")) {
      return {
        icon: <FaExclamationTriangle className="text-amber-500 w-4 h-4" />,
        bg: "bg-amber-50 border-amber-200",
        text: "text-amber-700",
      };
    }
    return {
      icon: <FaInfoCircle className="text-blue-500 w-4 h-4" />,
      bg: "bg-blue-50 border-blue-200",
      text: "text-blue-700",
    };
  };

  const renderStepDetails = (step = {}) => {
    const hiddenKeys = new Set(["type", "message", "createdInfo"]);
    const entries = Object.entries(step).filter(
      ([key, value]) => !hiddenKeys.has(key) && value !== undefined
    );
    if (entries.length === 0) return null;
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3 text-sm">
        {entries.map(([key, value]) => (
          <div key={key} className="flex flex-col">
            <span className="text-xs text-gray-500">{getStepKeyLabel(key)}</span>
            <span className="text-gray-800 break-words">
              {formatStepValue(key, value)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  // --- Tablo hücre render ---

  const renderTableCell = (item, column) => {
    switch (column.key) {
      case "date":
        return <span className="text-sm font-semibold text-gray-800">{item.date}</span>;
      case "hour":
        return <span className="text-sm text-gray-600">{item.hour}</span>;
      case "productName":
        return (
          <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-md text-xs font-semibold">
            {item.productName}
          </span>
        );
      case "consumption":
        return <span className="text-sm font-bold text-green-600">{item.consumption}</span>;
      case "products":
        return (
          <div className="inline-flex flex-col items-start gap-1 text-left">
            {item.products && Object.entries(item.products).map(([product, total]) => (
              <div key={product} className="flex items-center gap-2 text-xs sm:text-sm">
                <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-semibold">{product}</span>
                <span className="font-bold text-gray-800">{total}</span>
              </div>
            ))}
          </div>
        );
      case "totalConsumption":
        return <span className="text-sm font-bold text-green-600">{item.totalConsumption}</span>;
      case "hourlyAvg":
        return <span className="text-sm font-medium text-teal-600">{item.hourlyAvg}</span>;
      case "dailyAvg":
        return <span className="text-sm font-medium text-blue-600">{item.dailyAvg}</span>;
      case "monthlyAvg":
        return <span className="text-sm font-medium text-purple-600">{item.monthlyAvg}</span>;
      case "name":
        return <span className="text-sm font-semibold text-gray-800">{item.name}</span>;
      case "lastName":
        return <span className="text-sm text-gray-600">{item.lastName}</span>;
      case "infoName":
        return (
          <span
            className="text-sm text-gray-700 line-clamp-3 block"
            title={item.infoName}
          >
            {item.infoName}
          </span>
        );
      case "valueFrom":
        return <span className="text-sm font-bold text-red-600">{item.valueFrom}</span>;
      case "valueTo":
        return <span className="text-sm font-bold text-green-600">{item.valueTo}</span>;
      case "operation":
        return (
          <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${item.operationColor || "bg-gray-100 text-gray-700"}`}>
            {item.operation}
          </span>
        );
      case "source":
        return (
          <span className={`px-2 py-0.5 rounded-md text-xs font-SemiBold whitespace-nowrap ${item.sourceColor || "bg-gray-100 text-gray-700"}`}>
            {item.source}
          </span>
        );
      case "target":
        // Makineye giden/alinan kota ile firma havuzu hareketi bir bakista
        // ayrilsin: makine seri no monospace rozet, havuz duz metin.
        return item.scope === "Device" ? (
          <span className="inline-flex items-center gap-1.5 min-w-0">
            <FaMobileAlt className="w-3 h-3 text-gray-400 shrink-0" />
            <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-md text-xs font-mono font-SemiBold">
              {item.serialNo}
            </span>
          </span>
        ) : (
          // scope "Firm" — degisen bakiye firma havuzu. Cihazdan iade ve
          // cevrimdisi mahsupta hareketi TETIKLEYEN makine de belli; onu
          // havuzun yaninda kucuk rozet olarak gosteriyoruz ki "hangi
          // makineden geldi" satiri okumadan gorulsun.
          <span className="inline-flex items-center gap-1.5 flex-wrap text-sm text-gray-700">
            <FaBuilding className="w-3 h-3 text-gray-400 shrink-0" />
            Firma havuzu
            {item.serialNo && item.serialNo !== "-" && (
              <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs font-mono">
                {item.quotaDelta >= 0 ? "←" : "→"} {item.serialNo}
              </span>
            )}
          </span>
        );
      case "quotaDeltaLabel":
        return (
          <span className={`text-sm font-Bold ${item.quotaDeltaColor || "text-gray-600"}`}>
            {item.quotaDeltaLabel}
          </span>
        );
      case "quotaFrom":
        return <span className="text-sm font-Medium text-gray-600">{item.quotaFrom}</span>;
      case "quotaTo":
        return <span className="text-sm font-Bold text-gray-800">{item.quotaTo}</span>;
      case "billNo":
        return <span className="text-sm font-medium text-gray-700">{item.billNo}</span>;
      case "productCode":
        return <span className="text-sm text-gray-600">{item.productCode}</span>;
      case "quota":
        return <span className="text-sm font-bold text-violet-600">{item.quota}</span>;
      case "income":
        return <span className="text-sm font-bold text-emerald-600">{item.income}</span>;
      case "serialNo":
        return (
          <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-md text-xs font-mono font-semibold">
            {item.serialNo}
          </span>
        );
      case "operationName":
        return (
          <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-md text-xs font-semibold">
            {item.operationName}
          </span>
        );
      case "description":
        return (
          <span
            className="text-sm text-gray-600 line-clamp-3 block"
            title={item.description}
          >
            {item.description}
          </span>
        );
      case "direction":
        return item.direction === "Gelen" ? (
          <span className="px-2 py-0.5 bg-green-50 text-green-700 rounded-md text-xs font-semibold whitespace-nowrap">
            {item.directionLabel || "Gelen"}
          </span>
        ) : item.direction === "Giden" ? (
          <span className="px-2 py-0.5 bg-orange-50 text-orange-700 rounded-md text-xs font-semibold whitespace-nowrap">
            {item.directionLabel || "Giden"}
          </span>
        ) : (
          <span className="text-sm text-gray-600">-</span>
        );
      case "deviceCell": {
        const devImg = DEVICE_IMAGES[item.deviceModel];
        return (
          <span className="inline-flex items-center gap-2.5 min-w-0">
            <span className="w-10 h-10 shrink-0 bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden">
              {devImg ? (
                <img src={devImg} alt={item.deviceModel} className="max-w-full max-h-full object-contain p-0.5" />
              ) : (
                <FaMobileAlt className="w-4 h-4 text-slate-400" />
              )}
            </span>
            <span className="inline-flex flex-col items-start gap-0.5 min-w-0">
              <span className="text-sm font-bold text-gray-900 font-mono">{item.serialNo}</span>
              <span className="text-xs text-gray-500 truncate max-w-[140px]">{item.deviceName}</span>
            </span>
          </span>
        );
      }
      case "firmMovement": {
        // Cihaz listesindeki durum rozetleriyle ayni gorsel dil:
        // Depo=mor FaWarehouse, Firma(musteri)=indigo FaBuilding, Bayi=mavi FaStore
        const firmChip = (name, type) => {
          const cfg =
            type === "storage"
              ? { Icon: FaWarehouse, cls: "bg-purple-50 text-purple-700 border-purple-200" }
              : type === "dealer"
              ? { Icon: FaStore, cls: "bg-blue-50 text-blue-700 border-blue-200" }
              : type === "customer"
              ? { Icon: FaBuilding, cls: "bg-indigo-50 text-indigo-700 border-indigo-200" }
              : { Icon: FaBuilding, cls: "bg-gray-50 text-gray-600 border-gray-200" };
          const { Icon, cls } = cfg;
          return (
            <span
              className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border text-xs font-semibold ${cls}`}
              title={name}
            >
              <Icon className="w-3 h-3 shrink-0" />
              <span className="truncate max-w-[130px]">{name}</span>
            </span>
          );
        };
        return (
          <span
            className="inline-flex items-center gap-1.5 flex-wrap"
            title={`${item.fromFirm} → ${item.toFirm}`}
          >
            {firmChip(item.fromFirm, item.fromFirmType)}
            <span className="font-bold text-indigo-500">→</span>
            {firmChip(item.toFirm, item.toFirmType)}
          </span>
        );
      }
      case "byName":
        return (
          <span className="inline-flex items-center gap-1.5 text-sm text-gray-700">
            <FaUser className="w-3 h-3 text-gray-400 shrink-0" />
            {item.byName}
          </span>
        );
      case "dateTime":
        return (
          <span className="inline-flex flex-col items-center leading-tight">
            <span className="text-sm font-semibold text-gray-800">{item.date}</span>
            <span className="text-xs text-gray-500">{item.hour}</span>
          </span>
        );
      case "note":
        return (
          <span
            className="text-sm text-gray-600 line-clamp-3 block"
            title={item.note}
          >
            {item.note}
          </span>
        );
      default:
        return <span className="text-sm text-gray-600">-</span>;
    }
  };

  // --- Search: cihaz pattern (fetchTrigger) ---

  const handleGetLogDataRef = useRef(handleGetLogData);
  useEffect(() => {
    handleGetLogDataRef.current = handleGetLogData;
  });

  const [fetchTrigger, setFetchTrigger] = useState(0);
  const pendingFetchRef = useRef(null);

  useEffect(() => {
    if (fetchTrigger > 0 && pendingFetchRef.current !== null) {
      pendingFetchRef.current = null;
      handleGetLogDataRef.current();
    }
  }, [fetchTrigger]);

  const handleSearch = useCallback(() => {
    if (!startDate || !endDate) return;
    if (isDetailReport) {
      if (selectedProduct) {
        detailReport.run({ id: firm?._id, startDate, endDate, productName: selectedProduct });
      }
      return;
    }
    setDate(startDate);
    setDateSecond(endDate);
    pendingFetchRef.current = true;
    setTimeout(() => {
      setFetchTrigger((prev) => prev + 1);
    }, 150);
  }, [startDate, endDate, setDate, setDateSecond, isDetailReport, selectedProduct, detailReport, firm?._id]);

  const handleClearFilters = () => {
    setInput((prev) => ({ ...prev, type: "ConsumentReport" }));
    handleFilterType("Day");
    const today = new Date();
    const firstDay = clampToCreation(new Date(today.getFullYear(), today.getMonth(), 1), minReportDate);
    setStartDate(firstDay);
    setEndDate(today);
    setDate(firstDay);
    setDateSecond(today);
  };

  const handleRowClick = (rowData) => {
    if (input.type === "Consument") {
      // Ham loglar satirda tasiniyor (`_logs`); ikinci istek yok.
      setConsumentRow(rowData || null);
      return;
    }
    if (input.type === "QuotaChange") {
      setSelectedQuotaLog(rowData || null);
      setShowQuotaDrawer(true);
      return;
    }
    if (input.type !== "Operation") return;
    setSelectedOperationLog(rowData?.originalData || rowData || null);
    setShowOperationDrawer(true);
  };

  const tableData = getTableData();
  const resetKey = `${input.type}-${filterType}-${tableData.length}`;
  const loading = isFetching || isLoading;

  const getExcelData = () => {
    if (tableData.length === 0) return [];

    if (input.type === "Consument") {
      if (filterType === "Hourly") {
        const rows = tableData.map((item) => ({
          Tarih: item.date,
          Saat: item.hour,
          Ürün: item.productName,
          Tüketim: item.consumption,
        }));
        const totalConsumption = tableData.reduce((acc, item) => acc + (item.consumption || 0), 0);
        rows.push({ Tarih: "Toplam", Saat: "-", Ürün: "-", Tüketim: totalConsumption });
        return rows;
      }
      const rows = tableData.map((item) => {
        const productSummary = item.products
          ? Object.entries(item.products).map(([p, t]) => `${p}: ${t}`).join(", ")
          : "-";
        const avgLabel = filterType === "Day" ? "Saatlik Ort." : filterType === "Month" ? "Günlük Ort." : "Aylık Ort.";
        const avgValue = item.hourlyAvg || item.dailyAvg || item.monthlyAvg || "0";
        return {
          Tarih: item.date,
          "Ürün Bazında Tüketim": productSummary,
          "Toplam Tüketim": item.totalConsumption,
          [avgLabel]: avgValue,
        };
      });
      const grandTotal = tableData.reduce((acc, item) => acc + (item.totalConsumption || 0), 0);
      rows.push({ Tarih: "Toplam", "Ürün Bazında Tüketim": "-", "Toplam Tüketim": grandTotal });
      return rows;
    }

    if (input.type === "UpdateInfo") {
      return tableData.map((item) => ({
        İsim: item.name,
        Soyisim: item.lastName,
        Bilgi: item.infoName,
        "İlk Değer": item.valueFrom,
        "Son Değer": item.valueTo,
        Tarih: item.date,
        Saat: item.hour,
      }));
    }

    if (input.type === "QuotaChange") {
      return tableData.map((item) => ({
        İşlem: item.source,
        "Nereye / Nereden": item.target,
        "Seri No": item.serialNo,
        Ürün: item.productName,
        Değişim: item.quotaDeltaLabel,
        Önceki: item.quotaFrom,
        Sonraki: item.quotaTo,
        "Firma Havuzu (Önce)": item.firmQuotaFrom,
        "Firma Havuzu (Sonra)": item.firmQuotaTo,
        "Fatura No": item.billNo,
        Açıklama: item.description,
        "İşlemi Yapan": item.byName,
        Tarih: item.date,
        Saat: item.hour,
      }));
    }

    if (input.type === "UpdateBill") {
      return tableData.map((item) => ({
        İsim: item.name,
        Soyisim: item.lastName,
        İşlem: item.operation,
        "Fatura No": item.billNo,
        Ürün: item.productName,
        Miktar: item.quota,
        Gelir: item.income,
        Tarih: item.date,
        Saat: item.hour,
      }));
    }

    if (input.type === "SettingChange") {
      return tableData.map((item) => ({
        "Seri No": item.serialNo,
        İsim: item.name,
        Soyisim: item.lastName,
        Ayar: item.infoName,
        "Eski Değer": item.valueFrom,
        "Yeni Değer": item.valueTo,
        Tarih: item.date,
        Saat: item.hour,
      }));
    }

    if (input.type === "Operation") {
      return tableData.map((item) => ({
        "Seri No": item.serialNo,
        Operasyon: item.operationName,
        Açıklama: item.description,
        Tarih: item.date,
        Saat: item.hour,
      }));
    }

    if (input.type === "DeviceMovement") {
      return tableData.map((item) => ({
        Yön: item.directionLabel || item.direction,
        "Seri No": item.serialNo,
        Cihaz: item.deviceName,
        "Hangi Firmadan": item.fromFirm,
        "Hangi Firmaya": item.toFirm,
        "İşlem Detayı": item.description,
        "İşlemi Yapan": item.byName,
        Not: item.note,
        Tarih: item.date,
        Saat: item.hour,
      }));
    }

    return [];
  };

  const reportTypeLabels = {
    Consument: "Tüketim",
    UpdateInfo: "Bilgi Değişikliği",
    UpdateBill: "Fatura Hareketi",
    SettingChange: "Ayar Değişiklikleri",
    Operation: "Operasyonlar",
    DeviceMovement: "Cihaz Hareketleri",
    QuotaChange: "Kota Değişikliği",
  };

  const excelFileName = `${reportTypeLabels[input.type] || "Rapor"}_${firm?.firmName || "Firma"}_${new Date().toISOString().split("T")[0]}.xlsx`;

  const excelConfig = {
    sheetName: "Firma Raporu",
    headerStyle: {
      font: { bold: true, color: { rgb: "FFFFFF" }, size: 12 },
      fill: { fgColor: { rgb: "667eea" } },
      alignment: { horizontal: "center" },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } },
      },
    },
    dataStyle: {
      font: { size: 11 },
      alignment: { horizontal: "center", wrapText: true },
      border: {
        top: { style: "thin", color: { rgb: "E0E0E0" } },
        bottom: { style: "thin", color: { rgb: "E0E0E0" } },
        left: { style: "thin", color: { rgb: "E0E0E0" } },
        right: { style: "thin", color: { rgb: "E0E0E0" } },
      },
    },
    alternateRowStyle: { fill: { fgColor: { rgb: "F8F9FA" } } },
  };


  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={trLocale}>
      <div className="flex flex-col h-full">
        {/* Kota Değişikliği Drawer — tabloya sığmayan alanlar burada.
            Özellikle otomatik dağıtım satırlarında FİRMA HAVUZUNUN önce/sonrası
            (firmQuotaFrom/To) yalnızca burada ve Excel'de görünür. */}
        {quotaDrawerMounted && (
          <div className="fixed inset-0 z-50">
            <div
              className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ease-in-out ${quotaDrawerEntered ? "opacity-100" : "opacity-0"}`}
              onClick={() => setShowQuotaDrawer(false)}
            />
            <div
              className={`absolute right-0 top-0 h-screen w-full sm:w-[560px] bg-white shadow-2xl border-l border-gray-200 flex flex-col transform ${quotaDrawerEntered ? "translate-x-0" : "translate-x-full"} transition-transform duration-300 ease-in-out`}
            >
              <div className="shrink-0 bg-indigo-600 text-white px-5 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="bg-white/10 rounded-lg p-2">
                      <FaExchangeAlt className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-base font-SemiBold">Kota Hareketi</div>
                      <div className="text-xs text-white/70">
                        {selectedQuotaLog?.source || "-"} · {selectedQuotaLog?.productName || "-"}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowQuotaDrawer(false)}
                    className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                  >
                    <FaTimes className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                {/* Degisim ozeti */}
                <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                  <div className="text-xs text-gray-500 mb-1">
                    {selectedQuotaLog?.scope === "Device"
                      ? `Makine bakiyesi (${selectedQuotaLog?.serialNo || "-"})`
                      : "Firma havuzu"}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-Medium text-gray-600">{selectedQuotaLog?.quotaFrom ?? "-"}</span>
                    <span className="text-gray-400">→</span>
                    <span className="text-sm font-Bold text-gray-900">{selectedQuotaLog?.quotaTo ?? "-"}</span>
                    <span className={`ml-1 text-sm font-Bold ${selectedQuotaLog?.quotaDeltaColor || "text-gray-600"}`}>
                      ({selectedQuotaLog?.quotaDeltaLabel})
                    </span>
                  </div>
                </div>

                {/* Cihaz satirlarinda firma havuzunun karsiligi */}
                {selectedQuotaLog?.scope === "Device" &&
                  selectedQuotaLog?.firmQuotaFrom !== "-" && (
                    <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
                      <div className="text-xs text-gray-500 mb-1">
                        Firma havuzu (aynı işlemde)
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-Medium text-gray-600">{selectedQuotaLog?.firmQuotaFrom}</span>
                        <span className="text-gray-400">→</span>
                        <span className="text-sm font-Bold text-gray-900">{selectedQuotaLog?.firmQuotaTo}</span>
                      </div>
                    </div>
                  )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs text-gray-500 mb-0.5">İşlem</div>
                    <span className={`inline-block px-2 py-0.5 rounded-md text-xs font-SemiBold ${selectedQuotaLog?.sourceColor || "bg-gray-100 text-gray-700"}`}>
                      {selectedQuotaLog?.source || "-"}
                    </span>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-0.5">Ürün</div>
                    <div className="text-sm text-gray-800">{selectedQuotaLog?.productName || "-"}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-0.5">Makine</div>
                    <div className="text-sm text-gray-800 font-mono">{selectedQuotaLog?.serialNo || "-"}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-0.5">Fatura No</div>
                    <div className="text-sm text-gray-800">{selectedQuotaLog?.billNo || "-"}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-0.5">İşlemi Yapan</div>
                    <div className="text-sm text-gray-800">{selectedQuotaLog?.byName || "-"}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-0.5">Tarih / Saat</div>
                    <div className="text-sm text-gray-800">
                      {selectedQuotaLog?.date || "-"} {selectedQuotaLog?.hour || ""}
                    </div>
                  </div>
                </div>

                <div>
                  <div className="text-xs text-gray-500 mb-1">Açıklama</div>
                  <div className="text-sm text-gray-800 whitespace-pre-wrap rounded-xl border border-gray-200 bg-white px-4 py-3">
                    {selectedQuotaLog?.description || "-"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Operasyon Drawer */}
        {opDrawerMounted && (
          <div className="fixed inset-0 z-50">
            <div
              className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ease-in-out ${opDrawerEntered ? "opacity-100" : "opacity-0"}`}
              onClick={() => setShowOperationDrawer(false)}
            />
            <div
              className={`absolute right-0 top-0 h-screen w-full sm:w-[600px] bg-white shadow-2xl border-l border-gray-200 flex flex-col transform ${opDrawerEntered ? "translate-x-0" : "translate-x-full"} transition-transform duration-300 ease-in-out`}
            >
              <div className="shrink-0 bg-indigo-600 text-white px-5 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="bg-white/10 rounded-lg p-2">
                      <FaCogs className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold">Operasyon Detayı</h3>
                      <div className="text-xs text-indigo-200">
                        {selectedOperationLog?.operationName === "CleaningSave"
                          ? "Temizlik"
                          : selectedOperationLog?.operationName === "BrewLogSave"
                            ? "Demleme Emri"
                            : selectedOperationLog?.operationName || "-"}
                        {selectedOperationLog?.serialNo && (
                          <span className="ml-2 bg-white/10 px-1.5 py-0.5 rounded text-[11px]">
                            {selectedOperationLog.serialNo}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowOperationDrawer(false)}
                    className="text-white/80 hover:text-white transition-colors p-1"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="shrink-0 p-5 space-y-3 border-b border-gray-100 overflow-hidden">
                <div className="rounded-xl border border-gray-200 p-3">
                  <h4 className="text-xs font-semibold text-gray-500 mb-1 flex items-center gap-1.5">
                    <FaClock className="w-3 h-3 text-blue-500" /> Zaman
                  </h4>
                  <div className="text-sm text-gray-800">{formatDateTime(selectedOperationLog?.createdInfo)}</div>
                </div>
                <div className="rounded-xl border border-gray-200 p-3">
                  <h4 className="text-xs font-semibold text-gray-500 mb-1 flex items-center gap-1.5">
                    <FaInfoCircle className="w-3 h-3 text-indigo-500" /> Açıklama
                  </h4>
                  <div className="text-sm text-gray-800 whitespace-pre-wrap">{displayLogText(selectedOperationLog?.description) || "-"}</div>
                </div>
                {selectedOperationLog?.metadata != null && typeof selectedOperationLog.metadata === "object" && Object.keys(selectedOperationLog.metadata).length > 0 && !isOperationHidingMeta(selectedOperationLog?.operationName) && (
                  <div className="rounded-xl border border-gray-200 p-3">
                    <h4 className="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-1.5">
                      <FaInfoCircle className="w-3 h-3 text-amber-500" /> Bilgiler
                    </h4>
                    <dl className="space-y-1.5 text-sm">
                      {Object.entries(selectedOperationLog.metadata)
                        .filter(([key]) => !METADATA_HIDDEN_KEYS.includes(key))
                        .map(([key, value]) => {
                          const label = getMetadataLabel(
                            key,
                            selectedOperationLog?.metadata?.productName,
                            selectedOperationLog?.operationName
                          );
                          let displayValue = value;
                          if (typeof value === "object" && value !== null) {
                            displayValue = JSON.stringify(value);
                          } else if (typeof value === "boolean") {
                            displayValue = value ? "Evet" : "Hayır";
                          } else if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) {
                            displayValue = formatDateTime(value);
                          } else {
                            displayValue = value != null ? String(value) : "-";
                          }
                          return (
                            <div key={key} className="flex gap-2">
                              <dt className="text-gray-500 shrink-0">{label}:</dt>
                              <dd className="text-gray-800 break-words">{displayValue}</dd>
                            </div>
                          );
                        })}
                    </dl>
                  </div>
                )}
              </div>
              <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                <div className="shrink-0 px-5 pt-3 pb-1">
                  <h4 className="text-xs font-semibold text-gray-500 flex items-center gap-1.5">
                    <FaCogs className="w-3 h-3 text-teal-500" /> Operasyon Adımları
                  </h4>
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-5">
                  {Array.isArray(selectedOperationLog?.operationSteps) && selectedOperationLog.operationSteps.length > 0 ? (
                    <div className="space-y-2">
                      {selectedOperationLog.operationSteps.map((step, index) => {
                        const style = getStepStyle(step?.type);
                        return (
                          <div key={index} className={`border rounded-lg p-2.5 ${style.bg}`}>
                            <div className="flex items-center gap-2">
                              {style.icon}
                              <span className={`text-xs font-medium ${style.text}`}>{getStepLabel(step?.type)}</span>
                              <span className="ml-auto text-[11px] text-gray-500 shrink-0">{formatDateTime(step?.createdInfo)}</span>
                            </div>
                            {step?.message && <div className="mt-1.5 text-xs text-gray-700">{step.message}</div>}
                            {renderStepDetails(step)}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-xs text-gray-400">Adım bulunamadı.</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Compact Filter Bar */}
        <div className="shrink-0 border-b border-gray-200 bg-gray-50/80 px-3 py-2.5 sm:px-4 sm:py-4">
          <div className="grid gap-3 items-end grid-cols-2 sm:grid-cols-4">
            <DateRangeField
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              size="sm"
              disablePortal
              className="contents"
              minDate={minReportDate}
            />
            <CustomDropdown
              label="Rapor Tipi"
              compact={true}
              value={reportTypeOptions.find((o) => o.value === input.type)}
              onChange={(v) => setInput((prev) => ({ ...prev, type: v.value }))}
              options={reportTypeOptions}
              placeholder="Tip seçin"
              showSearch={false}
            />
            {/* FİRMA KAPSAMI — "Tüm Firmalar" (varsayılan) ya da tek firma.
                Yalnızca alt firması olan firmada gösterilir; sunucu boş
                `firmIds` gelince kök + tüm alt firmaları kapsar. */}
            {/* Tüketim raporu tüm kapsamı kendisi kırar (Firmalar bölümü) */}
            {!isDetailReport && firmScopeOptions.length > 2 && (
              <CustomDropdown
                label="Firma"
                compact={true}
                value={
                  firmScopeOptions.find((o) => o.value === selectedScopeFirm) ||
                  firmScopeOptions[0]
                }
                onChange={(v) => {
                  setSelectedScopeFirm(v.value);
                  // "" = tümü -> sunucuya boş liste gider.
                  setFirmIds(v.value ? [v.value] : []);
                }}
                options={firmScopeOptions}
                placeholder="Firma"
              />
            )}
            {isDetailReport && (
              <CustomDropdown
                label="Ürün"
                required={true}
                compact={true}
                value={productOptions.find((o) => o.value === selectedProduct)}
                onChange={(v) => setSelectedProduct(v.value)}
                options={productOptions}
                placeholder="Ürün seçin"
                showSearch={false}
              />
            )}
            {/* Saatlik/günlük gruplama yalnızca Tüketim raporunda kullanılır —
                diğer tiplerde dropdown gizlenir */}
            {input.type === "Consument" && (
              <CustomDropdown
                label="Filtreleme"
                compact={true}
                value={filterTypeOptions.find((o) => o.value === filterType)}
                onChange={(v) => handleFilterType(v.value)}
                options={filterTypeOptions}
                placeholder="Filtre"
                showSearch={false}
              />
            )}
          </div>

          <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200/60">
            <span className="hidden sm:inline text-sm text-gray-400">
              {tableData.length > 0
                ? <><span className="font-semibold text-gray-600">{tableData.length}</span> kayıt bulundu</>
                : "Filtreleri seçip raporu getirin"
              }
            </span>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <button
                onClick={handleClearFilters}
                className="flex items-center justify-center gap-1.5 px-3 py-2 sm:px-4 sm:py-2.5 text-xs sm:text-sm font-semibold text-gray-600 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 hover:text-gray-800 transition-colors flex-1 sm:flex-none"
              >
                <FaUndoAlt className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                <span>Temizle</span>
              </button>
              <button
                onClick={handleSearch}
                disabled={loading || detailReport.isLoading || !startDate || !endDate || (isDetailReport && !selectedProduct)}
                className="flex items-center justify-center gap-1.5 sm:gap-2 px-3 py-2 sm:px-5 sm:py-2.5 text-xs sm:text-sm font-semibold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 transition-colors shadow-sm disabled:bg-gray-300 disabled:shadow-none disabled:cursor-not-allowed flex-1 sm:flex-none"
              >
                {loading ? (
                  <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <FaSearch className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                )}
                <span>Raporu Getir</span>
              </button>
              {reportTypeOptions.length > 0 && !isDetailReport && (
                <ExportExcel
                  excelData={getExcelData()}
                  fileName={excelFileName}
                  {...excelConfig}
                  renderTrigger={(exportFn) => (
                    <button
                      onClick={exportFn}
                      disabled={tableData.length === 0}
                      className="flex items-center justify-center gap-1.5 sm:gap-2 px-3 py-2 sm:px-4 sm:py-2.5 text-xs sm:text-sm font-semibold text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 transition-colors shadow-sm disabled:bg-gray-300 disabled:shadow-none disabled:cursor-not-allowed flex-1 sm:flex-none"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span>Excel İndir</span>
                    </button>
                  )}
                />
              )}
            </div>
          </div>
        </div>

        {/* Tüketim (#343): Müşteri Performans raporu — önizleme önce Excel */}
        {isDetailReport && (
          <div className="flex-1 min-h-0 flex flex-col p-3 sm:p-4">
            <PerformanceReportView
              report={detailReport.report}
              isLoading={detailReport.isLoading}
              error={detailReport.error}
            />
          </div>
        )}

        {/* Content Area (Tüketim raporunda gizli) */}
        <div className={`flex-1 min-h-0 flex flex-col ${isDetailReport ? "hidden" : ""}`}>
          {loading && (
            <div className="flex-1 flex flex-col">
              <div className="flex items-center gap-3 px-4 py-2.5 bg-gray-50 border-b border-gray-100">
                {getTableColumns().map((col, i) => (
                  <div key={i} className={`h-3 bg-gray-200 rounded animate-pulse ${col.align === "left" ? "flex-[2]" : "flex-1"}`} />
                ))}
              </div>
              <div className="flex-1 px-4 py-2">
                {Array.from({ length: 8 }).map((_, idx) => (
                  <div key={idx} className="flex items-center gap-3 py-3 border-b border-gray-50">
                    {getTableColumns().map((col, i) => (
                      <div
                        key={i}
                        className={`h-4 bg-gray-100 rounded animate-pulse ${col.align === "left" ? "flex-[2]" : "flex-1"}`}
                        style={{ animationDelay: `${idx * 80 + i * 40}ms` }}
                      />
                    ))}
                  </div>
                ))}
              </div>
              <div className="shrink-0 flex items-center justify-between px-4 py-2.5 border-t border-gray-100">
                <div className="h-3 w-28 bg-gray-200 rounded animate-pulse" />
                <div className="flex items-center gap-1.5">
                  <div className="h-6 w-14 bg-gray-200 rounded-lg animate-pulse" />
                  <div className="h-6 w-6 bg-gray-200 rounded-lg animate-pulse" />
                  <div className="h-6 w-14 bg-gray-200 rounded-lg animate-pulse" />
                </div>
              </div>
            </div>
          )}

          {!loading && isError && (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center px-4">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-red-50 flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <p className="text-sm font-medium text-gray-700">Rapor yüklenirken hata oluştu</p>
                <p className="text-xs text-gray-400 mt-1">Lütfen tekrar deneyin.</p>
              </div>
            </div>
          )}

          {!loading && !isError && tableData.length > 0 && (
            <div className="flex-1 min-h-0 flex flex-col">
              <ReportTable
                title="Firma Raporu"
                count={tableData.length}
                countText="kayıt"
                columns={getTableColumns()}
                data={tableData}
                renderCell={renderTableCell}
                emptyMessage="Rapor verisi bulunamadı."
                paginate={true}
                defaultPageSize={25}
                pageSizeOptions={[10, 25, 50, 100]}
                resetKey={resetKey}
                compact={true}
                showHeader={false}
                className="flex-1"
                onRowClick={
                  input.type === "Operation" ||
                  input.type === "Consument" ||
                  input.type === "QuotaChange"
                    ? handleRowClick
                    : undefined
                }
              />
            </div>
          )}

          <ConsumentDeviceDrawer
            open={!!consumentRow}
            onClose={() => setConsumentRow(null)}
            row={consumentRow}
          />

          {!loading && !isError && tableData.length === 0 && (
            <div className="flex-1 flex items-center justify-center select-none">
              <div className="text-center px-4">
                <img
                  src={mascotNoData}
                  alt="Veri bulunamadı"
                  className="w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 object-contain mx-auto mb-4 drop-shadow-lg"
                  draggable={false}
                />
                <p className="text-base sm:text-lg font-bold text-gray-700">Veri bulunamadı</p>
                <p className="text-sm text-gray-400 mt-1">Filtreleri değiştirip tekrar deneyin.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </LocalizationProvider>
  );
};

export default ReportTab;
